import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { clearNewGroup, createGroup, selectNewGroup } from '@actions/groups';
import { metaTrigger } from '@support/keyboardHandler';
import Group from '@models/Group';
import { useNavigate } from 'react-router-dom';
import analytics from '@support/analytics';
import { useHotkeyHandler } from '@hooks/use-hotkey-handler';
import { useNavigationConfirmation } from '@hooks/use-navigation-confirmation';
import { alertUser } from '@actions/alerts';
import { GroupDisplay, unsavedChangesMsg } from '.';

const emptyComparisonGroup = new Group();

export default function NewGroup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const groupInStore = useSelector(selectNewGroup);
  const [group, setGroup] = useState(new Group());
  const [isEditingContacts, setIsEditingContacts] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const saveGroup = useCallback(() => {
    if (!isSaving && group.isDifferent(emptyComparisonGroup)) {
      dispatch(createGroup(group));
      setIsSaving(true);
      analytics.track('Group Created');
    }
  }, [group, isSaving]);

  useHotkeyHandler(
    {
      handler: (e) => {
        if (e.key === 'a' || e.key === '+') {
          e.preventDefault();
          setIsEditingContacts(true);
        } else if (metaTrigger(e) && e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
          saveGroup();
        } else if (e.key == 'Escape' && !group.isDifferent(emptyComparisonGroup)) {
          navigate(-1);
        }
      },
    },
    [group, saveGroup],
  );

  useNavigationConfirmation(
    {
      hasUnsavedChanges: !isSaving && group.isDifferent(emptyComparisonGroup),
      unsavedChangesMsg: unsavedChangesMsg(group, groupInStore),
      saveChanges: saveGroup,
    },
    [isSaving, group],
  );

  useEffect(() => {
    if (groupInStore.isPersisted) {
      navigate(`/group/${groupInStore.id}`);
    } else if (groupInStore.error) {
      setIsSaving(false);
      dispatch(clearNewGroup());
      dispatch(alertUser('Something went wrong saving this Group, please try again!', 'error'));
    }
  }, [groupInStore, navigate]);

  useEffect(
    () => () => {
      dispatch(clearNewGroup());
    },
    [],
  );

  return (
    <GroupDisplay
      group={group}
      isEditingContacts={isEditingContacts}
      saveGroup={saveGroup}
      referenceGroup={emptyComparisonGroup}
      startGroupContactEditing={() => setIsEditingContacts(true)}
      stopGroupContactEditing={() => setIsEditingContacts(false)}
      updateGroupInView={setGroup}
      isEditing
      isSaving={isSaving}
    />
  );
}
